import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import { BUILD_STATUSES } from "@consts";
import { isPhoneAssistant } from "@utils";
import useAppStore from "src/stores/appStore";
import { isAssistantPreviewableSelector, assistantIdSelector, selectedAssistantSelector, hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import { isUpdatingCollectionSelector } from "src/stores/selectors/collectionsSelectors";
import { hasFailedPollingDraftStatusSelector, previewLiveBuildResponseSelector, previewDraftBuildResponseSelector, previewLiveStatusPollingInProgressSelector, pollPreviewStatusSelector } from "src/stores/selectors/previewSelectors";
import { useDraft } from "src/queries/hooks/assistants";
import { useState, useEffect } from "react";
import { PREVIEW_TOOLTIP } from "./PreviewButton.consts";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";

export const usePreviewButton = () => {
  const assistant = useAppStore(selectedAssistantSelector);
  const assistantId = useAppStore(assistantIdSelector);
  const systemMode = useAppStore((state) => state.systemMode);
  const { data: draft, isLoading: isLoadingDraft } = useDraft();
  const isFirstDraft = draft?.isFirstDraft;
  const isAssistantPreviewable = useAppStore(isAssistantPreviewableSelector);
  const previewLiveStatusPollingInProgress = useAppStore(previewLiveStatusPollingInProgressSelector);
  const isUpdatingCollection = useAppStore(isUpdatingCollectionSelector);
  const previewLiveBuildResponse = useAppStore(previewLiveBuildResponseSelector);
  const previewDraftBuildResponse = useAppStore(previewDraftBuildResponseSelector);
  const hasFailedPollingDraftStatus = useAppStore(hasFailedPollingDraftStatusSelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const assistantsToDisablePreview = import.meta.env.VITE_ASSISTANTS_TO_DISABLE_CHAT_PREVIEW?.split(",");
  const accountId = useAppStore(state => state.selectedAccountId);
  const dashboardSettings = useFetchDashboardSettings({ enabled: !!accountId })?.data;

  const [isBuilding, setIsBuilding] = useState(false);
  const isPhone = isPhoneAssistant(assistant?.channel);
  const isReadOnlyMode = systemMode === SYSTEM_MODES.READ_ONLY;
  const isEditMode = systemMode === SYSTEM_MODES.EDIT;
  const isAssistantInDisableList = assistantId && assistantsToDisablePreview?.includes(assistantId);
  const hasNoPreviewBuildStatus = !previewLiveBuildResponse;
  const isDraftNotLive = draft && previewDraftBuildResponse?.status !== BUILD_STATUSES.LIVE;

  const isLoadingPreview = isReadOnlyMode && !isFirstDraft && (isLoadingDraft || hasNoPreviewBuildStatus);

  const shouldPreparePreview =
        isReadOnlyMode &&
        !isFirstDraft &&
        ["", BUILD_STATUSES.TERMINATED, BUILD_STATUSES.FAILED].includes(previewLiveBuildResponse?.status);

  const isAssistantNotPreviewable = isReadOnlyMode && isFirstDraft;

  const isPreparingPreview = !isFirstDraft &&
        isReadOnlyMode &&
        previewLiveStatusPollingInProgress &&
        previewLiveBuildResponse?.status === BUILD_STATUSES.PENDING;

  const hasEditModeRestrictions = isEditMode && (
    isDraftNotLive ||
        !draft ||
        hasFailedPollingDraftStatus
  );

  const hasReadOnlyModeRestrictions = isReadOnlyMode && isFirstDraft;

  const isPreviewDisabled = hasReadOnlyModeRestrictions ||
        isLoadingDraft ||
        hasEditModeRestrictions ||
        !isAssistantPreviewable ||
        isAssistantInDisableList ||
        isUpdatingCollection ||
        isPreparingPreview ||
        (
          shouldPreparePreview && !(
            hasPermission(USER_PERMISSIONS.ASSISTANTS.WRITE) ||
            hasPermission(USER_PERMISSIONS.ABILITIES.PREPARE_PREVIEW)
          )
        ) ||
        (shouldPreparePreview && dashboardSettings?.disablePublish) ||
        (shouldPreparePreview && dashboardSettings?.disableChanges);

  useEffect(() => {
    if (previewLiveBuildResponse?.status === BUILD_STATUSES.PENDING) {
      setIsBuilding(false);
    }
  }, [previewLiveBuildResponse]);

  const handlePreviewClick = (e, handleOpenDropdown, openPreparePreviewDialog) => {
    if (shouldPreparePreview) {
      openPreparePreviewDialog();
    } else {
      handleOpenDropdown(e);
    }
  };

  const getTooltipText = () => {
    if (shouldPreparePreview) {
      return (
        hasPermission(USER_PERMISSIONS.ASSISTANTS.WRITE) ||
        hasPermission(USER_PERMISSIONS.ABILITIES.PREPARE_PREVIEW)
      ) ? PREVIEW_TOOLTIP.SHOULD_PREPARE_PREVIEW : "";
    }
    if (isPreparingPreview) return PREVIEW_TOOLTIP.PREPARING_PREVIEW;
    if (isAssistantNotPreviewable) return PREVIEW_TOOLTIP.NOT_PREVIEWABLE;
    if (isEditMode) {
      if (!draft || isUpdatingCollection) return PREVIEW_TOOLTIP.NO_CHANGES;
      if (draft && previewDraftBuildResponse?.status !== BUILD_STATUSES.LIVE) return PREVIEW_TOOLTIP.BUILD_NEEDED;
    }
    if (!isLoadingPreview) return PREVIEW_TOOLTIP.PREVIEW_READY;
    return "";
  };

  return {
    isPreviewDisabled,
    shouldPreparePreview,
    isPreparingPreview,
    isLoadingPreview,
    isAssistantNotPreviewable,
    isPhone,
    isBuilding,
    setIsBuilding,
    handlePreviewClick,
    getTooltipText
  };
};

